import React from 'react';
import GeneralStatus from '../../GeneralStatus';
import { BattlePlayerRankCorner } from '../../pvp/battlePlayerRankCorner';
import { MOBILE_OPPONENT_AVATARS_TOP_PX } from './mobileBattleLayoutOffsets';
import { getTitleColorHex } from '../../constants/titleColors';

const ENLARGED_OPPONENT_AVATAR_NAMES = new Set([
  'avatar_peacock',
  'avatar_peackock',
  'avatar_boss_1',
  'avatar_gallmuecke',
  'avatar_kraken',
  'kangaroo_profile',
]);

function shouldEnlargeOpponentAvatar(avatarName) {
  if (!avatarName || typeof avatarName !== 'string') {
    return false;
  }
  const normalizedAvatarName = avatarName
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/\.(png|jpg|jpeg|webp)$/i, '');
  return ENLARGED_OPPONENT_AVATAR_NAMES.has(normalizedAvatarName);
}

function getOpponentAvatarScale(avatarName) {
  return shouldEnlargeOpponentAvatar(avatarName) ? 1.65 : 1;
}

/** Match desktop OpponentHUD profile ring: +20% avatar scale inside the circle (see BattleUI). */
const OPPONENT_HUD_PROFILE_RING_SCALE_MULTIPLIER = 1.2;

/**
 * Mobile Opponent Avatars (General and Advisor) Component
 * Positioned at top-center of the screen
 */
export const MobileOpponentAvatars = ({
  opponent,
  opponentGeneral,
  opponentUsername,
  opponentDamageFlash,
  avatars,
  yourTurn,
  attackMode,
  selectedAttacker,
  attackGeneral,
  getOpponentAvatarImage,
  getDamageFlashStyles,
  shouldMirrorOpponentAvatar,
  calculateFactionFromStrategy,
  getTranslatedFactionName,
  activeGameState,
  activeUserId,
  opponentHealthDisplayText,
  opponentRank,
  opponentPvpLadder,
  getRankStyle,
  /** false for bot PvP with no mock; true when human or when dev rank mock tests badges vs bots */
  allowOpponentRankCorner = true,
  resolveBattleTitleDisplay,
  barrierVisualResetKey = null,
}) => {
  return (
    <div 
      className="opponent-avatars-container-mobile"
      style={{
        position: "absolute",
        top: `${MOBILE_OPPONENT_AVATARS_TOP_PX}px`,
        left: "50%",
        transform: "translateX(-50%) scale(0.5)",
        transformOrigin: "center top",
        display: "flex",
        gap: "5px",
        alignItems: "center",
        zIndex: 1000
      }}
      onClick={() => {
        if (yourTurn && attackMode && selectedAttacker) {
          attackGeneral(selectedAttacker.zone, selectedAttacker.idx);
        }
      }}
    >
      {/* Opponent General with Health Orb and Bar — mirror desktop BattleUI orb + label geometry */}
      <div style={{ position: "relative" }} className="opponent-health-orb-container-mobile">
        <style>{`
          .opponent-health-orb-container-mobile .compact-orb {
            left: -70px !important;
          }
        `}</style>
        <GeneralStatus
          health={opponent.health}
          healthDisplayText={opponentHealthDisplayText}
          intelligenceLeft={opponent.intelligence}
          intelligenceTotal={opponent.maxIntelligence ?? 8}
          barriersRemoved={opponent.barriersRemoved ?? []}
          barrierVisualResetKey={barrierVisualResetKey}
          damageFlash={opponentDamageFlash}
          compact={true}
          showIntelligence={false}
          factionName={calculateFactionFromStrategy(opponent.strategy)}
          translatedFactionName={getTranslatedFactionName(calculateFactionFromStrategy(opponent.strategy))}
          isActiveTurn={!yourTurn}
        />
        {opponentUsername && (
          <div
            style={{
              position: "absolute",
              top: "calc(50% + 25px)",
              left: "-50px",
              transform: "translateX(-50%)",
              width: "fit-content",
              minWidth: "40px",
              textAlign: "center",
              color: "white",
              fontSize: "11px",
              fontWeight: "bold",
              textShadow: "1px 1px 2px rgba(0, 0, 0, 0.8)",
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              padding: "2px 4px",
              borderRadius: "4px",
              whiteSpace: "nowrap",
              zIndex: 10001,
              pointerEvents: "none"
            }}
          >
            {opponentUsername}
          </div>
        )}
        <div
          style={{
            position: "relative",
            width: "90px",
            height: "90px",
            display: "inline-block",
            overflow: "visible"
          }}
        >
          <img
            src={getOpponentAvatarImage(opponentGeneral)}
            alt="Opponent General"
            data-zone="opponent_general"
            data-target="opponent-general"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              display: "block",
              ...getDamageFlashStyles(opponentDamageFlash),
              transform: shouldMirrorOpponentAvatar(opponentGeneral, 'upper_middle', 'general', avatars)
                ? `scale(${getOpponentAvatarScale(opponentGeneral) * OPPONENT_HUD_PROFILE_RING_SCALE_MULTIPLIER}) scaleX(-1)`
                : `scale(${getOpponentAvatarScale(opponentGeneral) * OPPONENT_HUD_PROFILE_RING_SCALE_MULTIPLIER})`
            }}
          />
          {allowOpponentRankCorner && getRankStyle && (
            <BattlePlayerRankCorner
              playerRank={opponentRank}
              playerPvpLadderRow={opponentPvpLadder}
              getRankStyle={getRankStyle}
              compact
            />
          )}
        </div>
      </div>
      
      {/* Opponent intel/title lane with no advisor portrait */}
      <div style={{ position: "relative" }} className="opponent-intel-orb-container-mobile">
        <style>{`
          .opponent-intel-orb-container-mobile .compact-intel-orb {
            right: -88px !important;
          }
        `}</style>
        <GeneralStatus
          health={opponent.health}
          intelligenceLeft={opponent.intelligence}
          intelligenceTotal={opponent.maxIntelligence ?? 8}
          barriersRemoved={opponent.barriersRemoved ?? []}
          barrierVisualResetKey={barrierVisualResetKey}
          damageFlash={opponentDamageFlash}
          compactIntelligence={true}
        />
        <div
          style={{
            position: "absolute",
            top: "calc(50% + 25px)",
            right: "-68px",
            transform: "translateX(50%)",
            width: "fit-content",
            minWidth: "40px",
            textAlign: "center",
            color: getTitleColorHex(activeGameState?.opponentTitleColors?.[activeUserId]),
            fontSize: "11px",
            fontWeight: "bold",
            textShadow: "1px 1px 2px rgba(0, 0, 0, 0.8)",
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            padding: "2px 4px",
            borderRadius: "4px",
            whiteSpace: "nowrap",
            zIndex: 10001,
            pointerEvents: "none"
          }}
        >
          {resolveBattleTitleDisplay(activeGameState?.opponentTitles?.[activeUserId])}
        </div>
      </div>
    </div>
  );
};

export default MobileOpponentAvatars;

